import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';
import { notebookPalette } from '@/lib/notebookTheme';

// Coffee stain component
export interface CoffeeStainProps {
  size?: 'small' | 'medium' | 'large';
  intensity?: 'light' | 'medium' | 'heavy';
  className?: string;
  position?: { top?: string; left?: string; right?: string; bottom?: string };
}

export const CoffeeStain: React.FC<CoffeeStainProps> = ({
  size = 'medium',
  intensity = 'light',
  className,
  position,
}) => {
  const sizes = { small: 50, medium: 80, large: 120 };
  const opacities = { light: 0.08, medium: 0.12, heavy: 0.18 };
  const dimension = sizes[size];
  const opacity = opacities[intensity];

  // Generate slightly randomized ellipse for natural look
  const seed = useMemo(() => Math.random(), []);
  const skewX = (seed - 0.5) * 10;
  const skewY = (seed - 0.5) * 5;

  return (
    <div
      className={cn('absolute pointer-events-none', className)}
      style={{
        width: dimension,
        height: dimension,
        ...position,
      }}
    >
      <svg width="100%" height="100%" viewBox="0 0 100 100">
        <defs>
          <radialGradient id={`coffee-gradient-${seed}`} cx="50%" cy="50%" r="50%">
            <stop offset="40%" stopColor="transparent" />
            <stop offset="50%" stopColor={notebookPalette.personality.coffeeStain} stopOpacity={opacity} />
            <stop offset="55%" stopColor={notebookPalette.personality.coffeeStain} stopOpacity={opacity * 1.5} />
            <stop offset="60%" stopColor={notebookPalette.personality.coffeeStain} stopOpacity={opacity} />
            <stop offset="65%" stopColor="transparent" />
          </radialGradient>
        </defs>
        <ellipse
          cx="50"
          cy="50"
          rx={45 + seed * 5}
          ry={40 + seed * 8}
          fill={`url(#coffee-gradient-${seed})`}
          transform={`skewX(${skewX}) skewY(${skewY})`}
        />
        {/* Inner ring for more realism */}
        <ellipse
          cx="50"
          cy="50"
          rx={30 + seed * 5}
          ry={25 + seed * 5}
          fill="none"
          stroke={notebookPalette.personality.coffeeStain}
          strokeWidth={0.5}
          strokeOpacity={opacity * 0.5}
          transform={`skewX(${skewX * 0.5}) skewY(${skewY * 0.5})`}
        />
      </svg>
    </div>
  );
};

// Tape mark component (scotch tape, washi tape, masking tape)
export interface TapeMarkProps {
  variant?: 'scotch' | 'washi' | 'masking';
  color?: string;
  width?: number;
  rotation?: number;
  className?: string;
}

export const TapeMark: React.FC<TapeMarkProps> = ({
  variant = 'scotch',
  color,
  width = 80,
  rotation = -5,
  className,
}) => {
  const getStyles = () => {
    switch (variant) {
      case 'washi':
        return {
          background: color || 'linear-gradient(135deg, rgba(253, 224, 71, 0.7), rgba(251, 191, 36, 0.5))',
          height: 20,
          pattern: true,
        };
      case 'masking':
        return {
          background: color || 'rgba(234, 225, 201, 0.85)',
          height: 24,
          pattern: false,
        };
      case 'scotch':
      default:
        return {
          background: color || 'rgba(255, 255, 255, 0.3)',
          height: 18,
          pattern: false,
        };
    }
  };

  const styles = getStyles();

  return (
    <motion.div
      initial={{ scale: 0.8, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ type: 'spring', stiffness: 300 }}
      className={cn('relative', className)}
      style={{
        width,
        height: styles.height,
        background: styles.background,
        transform: `rotate(${rotation}deg)`,
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      }}
    >
      {/* Torn edges for tape effect */}
      <div
        className="absolute inset-y-0 left-0 w-1"
        style={{
          background: 'repeating-linear-gradient(180deg, transparent, transparent 2px, rgba(0,0,0,0.05) 2px, rgba(0,0,0,0.05) 4px)',
        }}
      />
      <div
        className="absolute inset-y-0 right-0 w-1"
        style={{
          background: 'repeating-linear-gradient(180deg, transparent, transparent 2px, rgba(0,0,0,0.05) 2px, rgba(0,0,0,0.05) 4px)',
        }}
      />
      {/* Washi pattern */}
      {styles.pattern && (
        <div
          className="absolute inset-0"
          style={{
            background: 'repeating-linear-gradient(45deg, transparent, transparent 4px, rgba(255,255,255,0.3) 4px, rgba(255,255,255,0.3) 8px)',
          }}
        />
      )}
    </motion.div>
  );
};

// Washi tape with predefined colors
export const WashiTape: React.FC<{
  color?: 'yellow' | 'pink' | 'blue' | 'green' | 'purple';
  width?: number;
  rotation?: number;
  className?: string;
  children?: React.ReactNode;
}> = ({ color = 'yellow', width = 120, rotation = -3, className, children }) => {
  const colors = {
    yellow: 'linear-gradient(135deg, rgba(253, 224, 71, 0.7), rgba(251, 191, 36, 0.5))',
    pink: 'linear-gradient(135deg, rgba(249, 168, 212, 0.7), rgba(236, 72, 153, 0.5))',
    blue: 'linear-gradient(135deg, rgba(147, 197, 253, 0.7), rgba(59, 130, 246, 0.5))',
    green: 'linear-gradient(135deg, rgba(134, 239, 172, 0.7), rgba(34, 197, 94, 0.5))',
    purple: 'linear-gradient(135deg, rgba(196, 181, 253, 0.7), rgba(139, 92, 246, 0.5))',
  };

  return (
    <div
      className={cn('relative inline-flex items-center justify-center px-4 py-1', className)}
      style={{
        background: colors[color],
        transform: `rotate(${rotation}deg)`,
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      }}
    >
      {/* Pattern overlay */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: 'repeating-linear-gradient(45deg, transparent, transparent 3px, rgba(255,255,255,0.2) 3px, rgba(255,255,255,0.2) 6px)',
        }}
      />
      {/* Torn edges */}
      <div className="absolute inset-y-0 left-0 w-1 bg-gradient-to-b from-transparent via-white/20 to-transparent" />
      <div className="absolute inset-y-0 right-0 w-1 bg-gradient-to-b from-transparent via-white/20 to-transparent" />
      <span className="relative font-handwritten text-gray-700">{children}</span>
    </div>
  );
};

// Sticky note component
export interface StickyNoteProps {
  color?: 'yellow' | 'pink' | 'blue' | 'green' | 'orange';
  rotation?: number;
  size?: 'small' | 'medium' | 'large';
  className?: string;
  children?: React.ReactNode;
  folded?: boolean;
}

export const StickyNote: React.FC<StickyNoteProps> = ({
  color = 'yellow',
  rotation = 2,
  size = 'medium',
  className,
  children,
  folded = true,
}) => {
  const colors = {
    yellow: { from: '#fef08a', to: '#fde047' },
    pink: { from: '#fce7f3', to: '#fbcfe8' },
    blue: { from: '#dbeafe', to: '#bfdbfe' },
    green: { from: '#dcfce7', to: '#bbf7d0' },
    orange: { from: '#ffedd5', to: '#fed7aa' },
  };

  const sizes = {
    small: 'w-24 h-24 p-2 text-xs',
    medium: 'w-32 h-32 p-3 text-sm',
    large: 'w-40 h-40 p-4 text-base',
  };

  const colorSet = colors[color];

  return (
    <motion.div
      initial={{ scale: 0.8, opacity: 0, rotate: rotation - 10 }}
      animate={{ scale: 1, opacity: 1, rotate: rotation }}
      transition={{ type: 'spring', stiffness: 200 }}
      className={cn(
        'relative font-handwritten',
        sizes[size],
        className
      )}
      style={{
        background: `linear-gradient(180deg, ${colorSet.from} 0%, ${colorSet.to} 100%)`,
        boxShadow: '2px 2px 8px rgba(0,0,0,0.15), inset 0 -2px 10px rgba(0,0,0,0.05)',
      }}
    >
      {/* Fold corner effect */}
      {folded && (
        <div
          className="absolute top-0 right-0 w-6 h-6"
          style={{
            background: `linear-gradient(135deg, transparent 50%, rgba(0,0,0,0.1) 50%)`,
          }}
        />
      )}
      {/* Content */}
      <div className="relative z-10 text-gray-700">{children}</div>
      {/* Adhesive strip hint at top */}
      <div
        className="absolute top-0 left-0 right-0 h-1"
        style={{
          background: `linear-gradient(180deg, rgba(0,0,0,0.05) 0%, transparent 100%)`,
        }}
      />
    </motion.div>
  );
};

// Paper clip component
export interface PaperClipProps {
  color?: 'silver' | 'gold' | 'red' | 'blue' | 'green';
  position?: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
  rotation?: number;
  className?: string;
}

export const PaperClip: React.FC<PaperClipProps> = ({
  color = 'silver',
  position = 'top-right',
  rotation = 15,
  className,
}) => {
  const colors = {
    silver: '#9ca3af',
    gold: '#d97706',
    red: '#dc2626',
    blue: '#2563eb',
    green: '#16a34a',
  };

  const positions = {
    'top-left': { top: -8, left: 16 },
    'top-right': { top: -8, right: 16 },
    'bottom-left': { bottom: -8, left: 16 },
    'bottom-right': { bottom: -8, right: 16 },
  };

  return (
    <motion.div
      initial={{ y: -20, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ type: 'spring', stiffness: 300 }}
      className={cn('absolute', className)}
      style={{
        ...positions[position],
        transform: `rotate(${rotation}deg)`,
      }}
    >
      <svg width="24" height="60" viewBox="0 0 24 60" fill="none">
        <path
          d="M12 2C8 2 4 6 4 12v32c0 6 4 10 8 10s8-4 8-10V16c0-4-2-6-4-6s-4 2-4 6v24"
          stroke={colors[color]}
          strokeWidth={3}
          strokeLinecap="round"
          fill="none"
        />
        {/* Highlight for 3D effect */}
        <path
          d="M12 4C9 4 6 7 6 12v32c0 5 3 8 6 8"
          stroke="white"
          strokeWidth={1}
          strokeOpacity={0.4}
          strokeLinecap="round"
          fill="none"
        />
      </svg>
    </motion.div>
  );
};

// Torn paper edge component
export const TornEdge: React.FC<{
  side?: 'top' | 'bottom' | 'left' | 'right';
  color?: string;
  className?: string;
}> = ({ side = 'bottom', color = '#fdfcf7', className }) => {
  const seed = useMemo(() => Math.random(), []);

  // Generate random tear pattern
  const generateTearPath = () => {
    const points: string[] = [];
    const steps = 20;

    if (side === 'top' || side === 'bottom') {
      for (let i = 0; i <= steps; i++) {
        const x = (i / steps) * 100;
        const y = side === 'top'
          ? Math.random() * 6 + 2
          : 8 - Math.random() * 6;
        points.push(`${x},${y}`);
      }
      if (side === 'top') {
        points.push('100,0', '0,0');
      } else {
        points.push('100,10', '0,10');
      }
    } else {
      for (let i = 0; i <= steps; i++) {
        const y = (i / steps) * 100;
        const x = side === 'left'
          ? Math.random() * 6 + 2
          : 8 - Math.random() * 6;
        points.push(`${x},${y}`);
      }
      if (side === 'left') {
        points.push('0,100', '0,0');
      } else {
        points.push('10,100', '10,0');
      }
    }

    return points.join(' ');
  };

  const isHorizontal = side === 'top' || side === 'bottom';
  const tearPath = useMemo(generateTearPath, [side, seed]);

  return (
    <div
      className={cn(
        'absolute pointer-events-none',
        side === 'top' && 'top-0 left-0 right-0',
        side === 'bottom' && 'bottom-0 left-0 right-0',
        side === 'left' && 'left-0 top-0 bottom-0',
        side === 'right' && 'right-0 top-0 bottom-0',
        className
      )}
      style={{
        height: isHorizontal ? 10 : '100%',
        width: isHorizontal ? '100%' : 10,
      }}
    >
      <svg
        width="100%"
        height="100%"
        viewBox={isHorizontal ? '0 0 100 10' : '0 0 10 100'}
        preserveAspectRatio="none"
      >
        <polygon points={tearPath} fill={color} />
      </svg>
    </div>
  );
};

// Notebook spine/binding component
export const NotebookSpine: React.FC<{
  className?: string;
  color?: string;
  rings?: number;
}> = ({ className, color = notebookPalette.cover.leather, rings = 5 }) => {
  return (
    <div
      className={cn('absolute left-0 top-0 bottom-0 w-8', className)}
      style={{ background: color }}
    >
      {/* Ring holes */}
      <div className="absolute inset-x-0 flex flex-col justify-around py-8">
        {Array.from({ length: rings }).map((_, i) => (
          <div
            key={i}
            className="mx-auto w-5 h-5 rounded-full bg-gray-300 shadow-inner"
            style={{ boxShadow: 'inset 2px 2px 4px rgba(0,0,0,0.2)' }}
          />
        ))}
      </div>
      {/* Spiral rings */}
      <svg className="absolute inset-0 w-full h-full" viewBox="0 0 32 100" preserveAspectRatio="none">
        {Array.from({ length: rings }).map((_, i) => {
          const y = ((i + 1) / (rings + 1)) * 100;
          return (
            <path
              key={i}
              d={`M 8,${y} C 24,${y - 2} 24,${y + 2} 8,${y}`}
              stroke="#9ca3af"
              strokeWidth={2}
              fill="none"
            />
          );
        })}
      </svg>
    </div>
  );
};

export default {
  CoffeeStain,
  TapeMark,
  WashiTape,
  StickyNote,
  PaperClip,
  TornEdge,
  NotebookSpine,
};
